import React, { useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
    Dropdown,
    SparkIcon,
    getOverlayPortalParent,
    useAnchoredPanelPosition,
    useEscapeKey,
    useOutsideClick,
} from '@spark';
import { useWalkthroughAutoOpen } from '@/features/walkthrough';

/** Walkthrough step that opens this menu while its mark is on the banner. */
export const MISSING_LABELS_WALKTHROUGH_STEP = 'missing-labels';

const menuItems = (isNer, isExistingTask) => [
    {
        label: `Update ${isNer ? 'entities' : 'labels'} in ${
            isExistingTask ? 'task' : 'Business Goals step'
        }`,
        action: 'addTaskLabels',
    },
    { label: 'Generate new data', action: 'generateNewData', disableWhenReadOnly: true },
    { label: 'Add more files', action: 'addMoreFiles', disableWhenReadOnly: true },
];

/**
 * AlertBanner "Fix" pill (Figma 3831:9395): opens a Spark Dropdown above the
 * button with actions for missing task labels.
 *
 * `isExistingTask` (Add Data modal on an existing task, vs the create-task
 * wizard) switches the update-catalog copy: the wizard has a Business Goals
 * step to point at, an existing task does not.
 *
 * The guided walkthrough opens the menu while its "missing labels" step is
 * on screen and closes it again when the step moves on.
 */
const MissingLabelsFixMenu = ({
    isNer = false,
    isExistingTask = false,
    onAddTaskLabels,
    onGenerateNewData,
    onAddMoreFiles,
}) => {
    const [open, setOpen] = useWalkthroughAutoOpen(MISSING_LABELS_WALKTHROUGH_STEP);
    const wrapRef = useRef(null);
    const panelRef = useRef(null);
    const close = useCallback(() => setOpen(false), []);

    useOutsideClick([wrapRef, panelRef], close, open);
    useEscapeKey(close, open);
    const panelStyle = useAnchoredPanelPosition(wrapRef, panelRef, open, 'end');
    const items = menuItems(isNer, isExistingTask);

    const handleSelect = (item) => {
        close();
        const action = items.find((option) => option.label === item.label)?.action;
        if (action === 'addTaskLabels') onAddTaskLabels?.();
        else if (action === 'generateNewData') onGenerateNewData?.();
        else if (action === 'addMoreFiles') onAddMoreFiles?.();
    };

    const panel = open ? (
        <div ref={panelRef} className="cw-dc-fix-menu-panel" style={panelStyle}>
            <Dropdown
                type="regular"
                showTitle={false}
                showFooter={false}
                maxVisibleItems={3}
                items={items.map(({ label, disableWhenReadOnly }) => ({ label, disableWhenReadOnly }))}
                onSelect={handleSelect}
            />
        </div>
    ) : null;

    return (
        <div ref={wrapRef} className="cw-dc-fix-menu">
            <button
                type="button"
                className="spark-alert-banner-action"
                aria-expanded={open}
                aria-haspopup="menu"
                onClick={() => setOpen((current) => !current)}
            >
                Fix
                <SparkIcon slug={open ? 'chevron-up' : 'chevron-down'} size="small" />
            </button>
            {open ? createPortal(panel, getOverlayPortalParent(wrapRef.current)) : null}
        </div>
    );
};

export default MissingLabelsFixMenu;
